import React from 'react';
import styled, { css } from 'styled-components';

import { useLast24hWalletPnLQuery } from 'api/queries/walletPositionsPnLQueries';
import { useWalletWithPositionsQuery } from 'api/queries/walletPositionsQueries';
import MaterialSymbol from 'components/basic/icons/MaterialSymbol';
import TextColored from 'components/basic/text/TextColored';
import { formatPercentage, formatUsdWithSign } from 'lib/display/formatting';

import { calculateWalletLast24hStat } from './walletLast24statsCalculation';

const Wrap = styled.div<{ $updating: boolean }>`
  font-size: var(--medium);
  line-height: 19px;
  color: #74777d;
  text-transform: initial;

  opacity: ${({ $updating }) => ($updating ? 0.9 : 1)};
`;

const iconStyle = css`
  position: relative;
  /* Manually position image with text */
  margin-top: -6px;
  margin-right: -2px;
  top: 5px;
`;

const ArrowDropUpStyled = styled(MaterialSymbol)`
  ${iconStyle}
`;

const ArrowDropDownStyled = styled(MaterialSymbol)`
  ${iconStyle}
`;

/**
 * How is PnL calculated in other products:
 * https://www.binance.com/en/support/faq/how-are-pnl-calculated-on-binance-futures-and-options-pnl-analysis-dbb171c4db1e4626863ec8bc545be46a
 * https://www.bybit.com/en/help-center/article/Profit-Loss-calculations-Inverse-Contracts#Unrealizedpl
 */
function WallletLast24hStat() {
  const { data: last24h, isRefetching: isRefetchingPnL } =
    useLast24hWalletPnLQuery();
  const { data: walletWithPositions, isRefetching: isRefetchingPositions } =
    useWalletWithPositionsQuery();

  // Hide data when loading - it might be because new deposit/withdrawal
  // which would cause data look incorrect
  if (last24h === undefined || !walletWithPositions || isRefetchingPnL) {
    return <Wrap $updating={false}>&nbsp;</Wrap>;
  }

  const last24hStats = calculateWalletLast24hStat(
    { ...last24h, refetching: isRefetchingPnL },
    {
      equity: walletWithPositions.equity,
      refetching: isRefetchingPositions,
    }
  );

  if (!last24hStats) {
    return <Wrap $updating={false}>&nbsp;</Wrap>;
  }

  const { last24hPnLAmount, last24hPnLPercentage } = last24hStats;

  const positive = last24hPnLAmount >= 0;
  const noChange = last24hPnLAmount === 0;

  const pnlAbsolute = Math.abs(last24hPnLAmount);

  const changedValue = (
    <>
      {noChange ? null : positive ? (
        <ArrowDropUpStyled icon="arrow_drop_up" fontSize="small" rounded />
      ) : (
        <ArrowDropDownStyled icon="arrow_drop_down" fontSize="small" rounded />
      )}
      {formatUsdWithSign(pnlAbsolute)} ({formatPercentage(last24hPnLPercentage)}
      %)
    </>
  );

  return (
    <Wrap $updating={isRefetchingPositions || isRefetchingPnL}>
      {noChange ? (
        changedValue
      ) : (
        <TextColored color={positive ? 'green' : 'red'}>
          {changedValue}
        </TextColored>
      )}{' '}
      Last 24H
    </Wrap>
  );
}

export default WallletLast24hStat;
